import { inject, Injectable } from "@angular/core";
import { from, Observable, of } from "rxjs";
import { catchError, map, mergeMap, tap } from "rxjs/operators";
import { MapService } from "../../../viewer-components/services/internal-services/map.service";

export interface DetectionRequestElement {
  label: string;
  detectionThreshold: number;
  maskThreshold?: number;
}

export interface DetectionRequest {
  wkt: string;
  srid?: number;
  elements: DetectionRequestElement[];
  /** Global buffer in metres applied to the entire WKT (expands the detection area outward). */
  bufferMeter?: number;
  /** Imagery collection override for this request's ortho extract. */
  collection?: string;
}

export interface DetectionApiFeature {
  type: "Feature";
  geometry: { type: string; coordinates: number[][][] };
  properties: {
    label: string;
    detections: Array<{
      imageId: string;
      confidence?: number;
      bbox?: string;
      "pixel-bbox"?: [number, number, number, number];
      "pixel-mask"?: string;
    }>;
  };
}

export interface DetectionSourceImage {
  "image-id": string;
  endpoint: string;
  metadata: {
    geometry?: string;
    srid?: number;
    layer?: string;
    collection?: string;
    "product-type"?: string;
    "pretty-name"?: string;
    width?: number;
    height?: number;
    "min-zoom"?: number;
    "max-zoom"?: number;
    [key: string]: unknown;
  };
}

export interface DetectionApiResponse {
  type: "FeatureCollection";
  features: DetectionApiFeature[];
  images?: DetectionSourceImage[];
}

/** One settled sub-request emitted by {@link DetectionCommunicatorService.detectAll}. */
export interface DetectionTileResponse {
  /** Index of the originating request (tile) in the array passed to `detectAll`. */
  index: number;
  /** That request's own response. For multi-request runs, image ids are namespaced `"{index}:"`. */
  response: DetectionApiResponse;
}

/** Maximum number of sub-geometry requests allowed to be in flight at once. */
const MAX_CONCURRENT_REQUESTS = 10;

/**
 * HTTP gateway for the elements detection API.
 * Delegates to `MapService.elementsDetect(...)`, which wraps the core-map-api
 * SDK call. This keeps all SDK access funnelled through MapService — the
 * canonical pattern in this repo (see extractAdvancedOrthoImage, etc.).
 *
 * Not `providedIn: "root"` — MapService is provided at the viewer component
 * scope, so this service must be registered there too (alongside
 * DetectionService) for the injector chain to resolve.
 */
@Injectable()
export class DetectionCommunicatorService {
  private readonly mapService = inject(MapService);

  detect(request: DetectionRequest): Observable<DetectionApiResponse> {
    return this.mapService
      .elementsDetect(this.buildPayload(request))
      .pipe(map((raw: unknown) => this.normalizeResponse(raw)));
  }

  /**
   * Runs N detection requests with at most {@link MAX_CONCURRENT_REQUESTS} in
   * flight at any given time (`mergeMap`'s concurrency limit) — as soon as one
   * request settles, the next queued one starts immediately, keeping the pool
   * saturated instead of waiting for a whole batch of 10 to finish. Individual
   * failures are swallowed and replaced with an empty response so one bad
   * sub-request never silently drops the rest.
   *
   * Emits **one {@link DetectionTileResponse} per settled request** — only that
   * request's own response, never a re-merge of everything received so far. The
   * consumer accumulates. Re-merging the full array on every emission made each
   * emission O(total detections so far) and, because `mergeMap` only dispatches
   * the next request after the downstream synchronous work returns, it throttled
   * the whole run down to ~1 request in flight on large areas.
   *
   * The optional `onRequestComplete` callback is invoked after every individual
   * request settles (success **or** error), receiving `(completedSoFar, total)`
   * so callers can drive a real progress indicator.
   *
   * When `requests` has exactly one element this delegates straight to
   * {@link detect}; its response is emitted as-is (no image-id namespacing).
   */
  detectAll(
    requests: DetectionRequest[],
    onRequestComplete?: (completed: number, total: number) => void,
  ): Observable<DetectionTileResponse> {
    const total = requests.length;

    if (total === 1) {
      return this.detect(requests[0]).pipe(
        map((response) => ({ index: 0, response })),
        tap(() => onRequestComplete?.(1, 1)),
      );
    }

    const empty: DetectionApiResponse = { type: "FeatureCollection", features: [], images: [] };
    let completed = 0;

    return from(requests).pipe(
      mergeMap(
        (request, index) =>
          this.detect(request).pipe(
            catchError((err) => {
              console.error("DetectionCommunicator: sub-request failed, continuing:", err);
              return of(empty);
            }),
            map((response) => ({ index, response: DetectionCommunicatorService.namespaceResponse(response, index) })),
            // Fire after catchError so both successes and failures advance the counter
            tap(() => {
              completed += 1;
              onRequestComplete?.(completed, total);
            }),
          ),
        MAX_CONCURRENT_REQUESTS,
      ),
    );
  }

  /**
   * Prefixes every `image-id` (and every detection's `imageId`) in a single response
   * with its request index (`"0:abc"`, `"1:abc"`, …).
   *
   * The server may reuse the same `image-id` string across different geographic
   * requests (e.g. two areas share the same ortho tile id but have different
   * metadata), so namespacing guarantees every detection resolves to the correct
   * source-image metadata. The prefix is also how `DetectionService` recovers a
   * detection's originating tile index.
   */
  private static namespaceResponse(response: DetectionApiResponse, index: number): DetectionApiResponse {
    const ns = `${index}:`;
    return {
      type: "FeatureCollection",
      images: (response.images ?? []).map((img) => ({ ...img, "image-id": `${ns}${img["image-id"]}` })),
      features: response.features.map((feat) => ({
        ...feat,
        properties: {
          ...feat.properties,
          detections: feat.properties.detections.map((det) => ({ ...det, imageId: `${ns}${det.imageId}` })),
        },
      })),
    };
  }

  /**
   * Builds the core-map-api elementsDetect payload from our internal request.
   *
   * - `bufferMeter` in the API is GLOBAL to the WKT (single value). The UI
   *   models per-object buffer; we collapse to the first defined one until
   *   we decide whether to split into N parallel calls or rework the UX.
   * - `include` adds "bbox" on top of the API defaults ("geometry,confidence")
   *   so the detection cards can overlay the per-detection rectangle.
   * - `images[0].request.layer` is the comma-separated list of Vexcel layers
   *   currently loaded on the map, so the backend restricts imagery selection
   *   to what the user actually has on screen.
   * - `images[0].request.order-by` picks the most recent collection first,
   *   then the image whose center is closest to the AOI.
   */
  private buildPayload(request: DetectionRequest): Record<string, unknown> {
    const layers = this.mapService.getOverlayManager()?.getVexcelSources() ?? [];

    return {
      wkt: request.wkt,
      ...(request.srid !== undefined ? { srid: String(request.srid) } : {}),
      ...(request.bufferMeter !== undefined ? { bufferMeter: request.bufferMeter } : {}),
      images: [
        {
          endpoint: "ortho/extract",
          request: {
            ...(request.collection ? { collection: request.collection } : {}),
            ...(layers.length > 0 ? { layer: layers.join(",") } : {}),
            "order-by": "collection-last-capture-date-desc",
          },
        },
      ],
      modelInput: {
        elements: request.elements.map((e) => ({
          label: e.label,
          detectionThreshold: e.detectionThreshold,
          ...(e.maskThreshold !== undefined ? { maskThreshold: e.maskThreshold } : {}),
        })),
        include: ["geometry", "confidence", "bbox", "pixel-bbox", "pixel-mask"],
      },
    };
  }

  /**
   * Defensive normalization — the SDK types the response as `Promise<any>`,
   * so we tolerate unexpected shapes and surface an empty FeatureCollection
   * (which the UI renders as the "no detections" state) instead of crashing.
   * The `console.warn` makes shape mismatches visible during integration.
   */
  private normalizeResponse(raw: unknown): DetectionApiResponse {
    if (!raw || typeof raw !== "object") {
      console.warn("DetectionCommunicator: empty/invalid response", raw);
      return { type: "FeatureCollection", features: [], images: [] };
    }
    const r = raw as DetectionApiResponse;
    if (r.type !== "FeatureCollection" || !Array.isArray(r.features)) {
      console.warn("DetectionCommunicator: unexpected response shape", raw);
      return { type: "FeatureCollection", features: [], images: [] };
    }
    return r;
  }
}
